import React, { useState, useEffect } from 'react';
import { Grid, TextField, Button, CircularProgress, FormControlLabel } from '@material-ui/core';
import { useForm, Controller } from 'react-hook-form';
import useCustomSnackbar from 'hooks/useCustomSnackbar';
import useCategoriesIM from 'hooks/useCategoriesIM';
import Checkbox from '@material-ui/core/Checkbox';
import NumberFormat from 'react-number-format';
import { ICategories } from 'interfaces/imCategories.interface';

interface Props {
  handleCloseModal(): void;
  handleOnSubmit(data: ICategories): Promise<boolean>;
  getAllCategories(): void;
  categoryId: string;
  isPromotion: boolean;
}

export default function IMCategoryForm({
  handleCloseModal,
  handleOnSubmit,
  categoryId,
  getAllCategories,
  isPromotion
}: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const { showNotistack } = useCustomSnackbar();
  const { handleSubmit, register, errors, reset, control } = useForm<ICategories>();
  const { getCategoryById } = useCategoriesIM();

  const onSubmit = (data: ICategories) => {
    if (categoryId) {
      data.id = categoryId;
    }

    const deletePercent = data.percentage.toString().replace('%', '');
    data.percentage = Number(deletePercent);

    setIsLoading(true);

    handleOnSubmit(data)
      .then((response: boolean) => {
        if (response === true) {
          showNotistack({ message: 'La categoría se guardó correctamente' });
          getAllCategories();
          handleCloseModal();
        } else {
          showNotistack({ message: 'Ocurrió un error' });
        }
      })
      .catch((err: any) => {
        console.log(err.reponse?.data);
        showNotistack({ message: 'Ocurrió un error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (categoryId) {
      setIsLoading(true);
      getCategoryById(categoryId)
        .then((resultCategory: ICategories) => {
          reset(resultCategory);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [categoryId]);

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Grid container direction="row" spacing={2} justify="center">
        <Grid item xs={12} sm={12} md={12}>
          <Grid container direction="row" spacing={2} justify="center">
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                required
                variant="outlined"
                margin="normal"
                fullWidth
                name="name"
                label="Categoría"
                autoComplete="name"
                autoFocus
                disabled={isLoading}
                inputRef={register<HTMLInputElement>({
                  required: 'Este campo es requerido'
                })}
                error={errors.name ? true : false}
                helperText={errors.name && errors.name.message}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="percentage"
                variant="outlined"
                control={control}
                defaultValue={''}
                rules={{
                  required: 'Este campo es requerido'
                }}
                as={
                  <NumberFormat
                    required
                    decimalSeparator={'.'}
                    decimalScale={2}
                    margin="normal"
                    fullWidth
                    type="text"
                    customInput={TextField}
                    variant="outlined"
                    label="Porcentaje"
                    allowNegative={false}
                    InputLabelProps={{ shrink: true }}
                    error={errors.percentage ? true : false}
                    disabled={isLoading}
                    helperText={errors.percentage && errors.percentage.message}
                    suffix="%"
                  />
                }
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Controller
                name="isPromotion"
                control={control}
                defaultValue={isPromotion}
                render={(props) => (
                  <Checkbox
                    color="primary"
                    onChange={(e) => props.onChange(e.target.checked)}
                    checked={props.value ? props.value : false}
                  />
                )}
              />
            }
            label="Es promoción?"
          />
        </Grid>

        <Button type="submit" variant="contained" color="primary" disabled={isLoading} style={{ marginTop: 20 }}>
          {isLoading ? <CircularProgress size={24} /> : ''} Guardar
        </Button>
        <Button
          variant="contained"
          color="default"
          disabled={isLoading}
          style={{ marginTop: 20, marginLeft: 20 }}
          onClick={handleCloseModal}
        >
          {isLoading ? <CircularProgress size={24} /> : ''} Cancelar
        </Button>
      </Grid>
    </form>
  );
}
